// The pre-call terms-and-conditions gate.
//
// Split out of element.ts because it is a modal dialog with its own DOM, its
// own focus contract, and its own persistence - none of which the call
// lifecycle needs to know about. What it exposes is one question: may this
// call start?
//
// The focus handling is the part worth keeping intact. A modal that traps
// Tab, restores focus on close, and closes on Escape is the difference
// between a usable gate and one that strands a keyboard user on somebody
// else's website, and every one of those behaviours is a separate listener
// with a separate teardown. They are pinned by element-a11y.test.ts.

/** Resolved markdown renderer, fetched on demand by the caller. */
export type MarkdownRenderer = (input: string) => string;

export interface TermsGateHost {
  /** The shadow root the modal lives in; used to read focus inside the shadow tree. */
  readonly shadowRoot: ShadowRoot | null;
  readonly isConnected: boolean;
  /** Focus target when nothing else was focused before the modal opened. */
  fallbackFocus(): HTMLElement | null;
}

/**
 * Builds the overlay, modal, and its two actions. Returned as a struct rather
 * than queried back out of the DOM so the element wires listeners against
 * typed references instead of selectors that can silently stop matching.
 */
export interface TermsElements {
  overlay: HTMLDivElement;
  body: HTMLDivElement;
  cancel: HTMLButtonElement;
  accept: HTMLButtonElement;
}

export function buildTermsDOM(onClose: (accepted: boolean) => void): TermsElements {
  const overlay = document.createElement("div");
  overlay.className = "terms-overlay";
  overlay.setAttribute("role", "presentation");
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) onClose(false);
  });

  const modal = document.createElement("div");
  modal.className = "terms-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Terms and conditions");
  modal.setAttribute("tabindex", "-1");

  const body = document.createElement("div");
  body.className = "terms-content";
  // tabindex makes the scroll container keyboard-reachable so users can
  // Page/Arrow-key through long terms text before choosing Accept.
  body.setAttribute("tabindex", "0");
  modal.appendChild(body);

  const actions = document.createElement("div");
  actions.className = "terms-actions";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "terms-action";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => onClose(false));

  const accept = document.createElement("button");
  accept.type = "button";
  accept.className = "terms-action primary";
  accept.textContent = "Accept";
  accept.addEventListener("click", () => onClose(true));

  actions.append(cancel, accept);
  modal.appendChild(actions);
  overlay.appendChild(modal);

  return { overlay, body, cancel, accept };
}

/**
 * Remembers acceptance per (agent, exact terms text). Hashing the content
 * rather than keying on the agent alone means editing the terms re-prompts
 * everyone who accepted the previous wording, which is the whole point of
 * having a gate.
 */
export function termsStorageKey(agentId: string | null, content: string): string {
  let hash = 5381;
  for (let i = 0; i < content.length; i += 1) {
    hash = ((hash << 5) + hash) ^ content.charCodeAt(i);
  }
  return `speechify-agent:terms:${agentId ?? "unknown-agent"}:${hash >>> 0}`;
}

/** True when this exact wording was already accepted on this device. */
export function alreadyAccepted(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    // Storage denied (privacy mode, sandboxed iframe). The gate still runs
    // for this page view; it just will not be remembered.
    return false;
  }
}

export function rememberAcceptance(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // Acceptance still applies to this call.
  }
}

/**
 * Drives the modal: shows it, traps focus while it is open, and resolves with
 * the visitor's answer.
 */
export class TermsGate {
  readonly #host: TermsGateHost;
  readonly #els: TermsElements;

  #resolve: ((accepted: boolean) => void) | null = null;
  #previouslyFocused: HTMLElement | null = null;

  constructor(host: TermsGateHost, els: TermsElements) {
    this.#host = host;
    this.#els = els;
  }

  get open(): boolean {
    return this.#els.overlay.classList.contains("open");
  }

  /**
   * Shows the rendered terms and waits for an answer. Resolves false if the
   * element was detached, or the agent swapped, while the caller was fetching
   * the markdown renderer - installing the modal then would either attach it
   * to a detached tree or show one agent's terms before another agent's call.
   */
  async prompt(html: string): Promise<boolean> {
    if (!this.#host.isConnected) return false;

    // Prefer shadowRoot.activeElement: document.activeElement returns the
    // shadow host when focus is inside a shadow tree, which is not tabbable
    // and fails to restore focus when the modal closes.
    const shadowActive = this.#host.shadowRoot?.activeElement;
    const docActive = this.#els.overlay.ownerDocument.activeElement;
    this.#previouslyFocused =
      (shadowActive instanceof HTMLElement ? shadowActive : null) ??
      (docActive instanceof HTMLElement ? docActive : null);

    this.#els.body.innerHTML = html;
    this.#els.overlay.classList.add("open");
    this.#els.overlay.ownerDocument.addEventListener("keydown", this.#onEscape);
    this.#els.overlay.ownerDocument.addEventListener("keydown", this.#onTab);
    this.#els.accept.focus();
    return new Promise<boolean>((resolve) => {
      this.#resolve = resolve;
    });
  }

  /** Closes the modal and answers any outstanding prompt. Safe to call when closed. */
  close(accepted: boolean): void {
    this.#els.overlay.classList.remove("open");
    this.#els.overlay.ownerDocument.removeEventListener("keydown", this.#onEscape);
    this.#els.overlay.ownerDocument.removeEventListener("keydown", this.#onTab);

    const focusTarget = this.#previouslyFocused ?? this.#host.fallbackFocus();
    this.#previouslyFocused = null;
    const resolve = this.#resolve;
    this.#resolve = null;
    resolve?.(accepted);
    // Deferred so the caller's own `finally` - which resumes after the
    // resolve above - can re-enable the button first: .focus() silently
    // no-ops on a disabled element.
    queueMicrotask(() => focusTarget?.focus());
  }

  #onEscape = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.close(false);
  };

  #onTab = (event: KeyboardEvent): void => {
    if (event.key !== "Tab" || !this.open) return;
    const focusable: HTMLElement[] = [this.#els.body, this.#els.cancel, this.#els.accept];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    const active = this.#host.shadowRoot?.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };
}
